/**
 * Discard the previous installation's secure credentials on the first launch
 * after install.
 *
 * iOS deletes the WKWebView cache (localStorage) when the app is removed, but a
 * Keychain item survives the uninstall. A reinstalled app therefore boots with a
 * refresh token and no user, company or permission cache to go with it: the
 * silent refresh authenticates that credential, the office shell mounts with an
 * empty RBAC list, and the dashboard and profile guards bounce the route between
 * each other. Every bounce asks for a permission that was never loaded, the
 * warning spam fills WKWebView's IPC queue, and iOS kills the web process as
 * misbehaving — the white screen that blinks for minutes after installing the
 * app, before the user ever sees a login form.
 *
 * The install marker lives in the WebView cache ON PURPOSE: it has to die with
 * the app, because its absence is the only signal that this container is new.
 * Moving it to the Keychain would outlive the uninstall and this purge would
 * never run again. A TestFlight / App Store *update* keeps the container, so the
 * marker stays and a working session is left untouched.
 *
 * `native` and `removeSecureKey` are injected rather than imported so this
 * module stays free of the Capacitor runtime and can be unit-tested on plain
 * node, the same way nativeSessionRights.js is.
 */

/** WebView-cache flag: this install has booted at least once. */
export const NATIVE_INSTALL_MARKER_KEY = 'native_install_seen'

/**
 * Secure-storage keys that can revive the previous install's session without a
 * fresh sign-in, plus the identity-gate stamp that must not carry over to a new
 * container. Spelled out here instead of imported from native/biometric.js,
 * native/passkey.js and native/identityGate.js because those modules pull in the
 * Capacitor plugins — tokenManager.js duplicates the same names for that reason.
 */
export const FIRST_LAUNCH_SECURE_KEYS = Object.freeze([
  'refresh_token',
  'biometric_refresh_token',
  'token_quick_login_phone',
  'passkey_phone',
  'last_biometric_at',
  'fsj_biometric_credentials',
])

/**
 * Whether this container has booted before.
 *
 * Storage that throws is reported as `unavailable` and never as a first launch:
 * iOS private mode and an exhausted quota both refuse reads, and treating that
 * as "new install" would destroy a working session's credential.
 *
 * @param {{getItem: (key: string) => string|null}} storage
 * @returns {'first-launch'|'existing-install'|'unavailable'}
 */
export function nativeInstallState(storage) {
  try {
    const marker = storage.getItem(NATIVE_INSTALL_MARKER_KEY)
    return marker ? 'existing-install' : 'first-launch'
  } catch {
    return 'unavailable'
  }
}

/**
 * Clear the leftover secure credentials when this is the first launch of a new
 * installation, then mark the container as seen. Never throws.
 *
 * @param {object} opts
 * @param {boolean} opts.native running inside a Capacitor shell
 * @param {{getItem: Function, setItem: Function}} opts.storage WebView localStorage
 * @param {(key: string) => Promise<boolean>} opts.removeSecureKey secureRemove()
 * @param {() => Promise<boolean>} [opts.clearAllSecureKeys] secureClearAllNative()
 * @param {readonly string[]} [opts.keys]
 * @returns {Promise<{purged: boolean, reason: string, removed: string[], markerPersisted: boolean, clearedAll: boolean}>}
 */
export async function purgeSecureCredentialsOnFirstLaunch({
  native,
  storage,
  removeSecureKey,
  clearAllSecureKeys,
  keys = FIRST_LAUNCH_SECURE_KEYS,
}) {
  const idle = { purged: false, removed: [], markerPersisted: false, clearedAll: false }

  // Web keeps its refresh token in localStorage under the very same names, so a
  // purge there would sign out every visitor on their first page load.
  if (!native) return { ...idle, reason: 'web' }

  const state = nativeInstallState(storage)
  if (state !== 'first-launch') {
    return { ...idle, reason: state === 'unavailable' ? 'storage-unavailable' : 'existing-install' }
  }

  let clearedAll = false
  if (typeof clearAllSecureKeys === 'function') {
    try {
      clearedAll = await clearAllSecureKeys() === true
    } catch {
      clearedAll = false
    }
  }

  const removed = []
  if (clearedAll) {
    removed.push(...keys)
  } else {
    let anyFailed = false
    for (const key of keys) {
      try {
        const ok = await removeSecureKey(key)
        if (ok) removed.push(key)
        else anyFailed = true
      } catch {
        anyFailed = true
      }
    }
    if (anyFailed && typeof clearAllSecureKeys === 'function') {
      try {
        if (await clearAllSecureKeys() === true) {
          clearedAll = true
          for (const key of keys) {
            if (!removed.includes(key)) removed.push(key)
          }
        }
      } catch {
        clearedAll = false
      }
    }
  }

  let markerPersisted = false
  try {
    storage.setItem(NATIVE_INSTALL_MARKER_KEY, '1')
    markerPersisted = true
  } catch {
    // Reported to the caller: without the marker the purge repeats next launch,
    // which is idempotent, and storage that cannot be written could not have
    // held the user/company cache this session needs anyway.
  }

  return { purged: true, reason: 'first-launch', removed, markerPersisted, clearedAll }
}
